import { Card, Typography } from "antd";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
	ATTENDANCE_STATUS_RANG,
	ColumnChart,
	Donut,
	holatVar,
	SegmentBar,
	Sparkline,
} from "@/shared/charts";
import { useReportAnalytics } from "../../hooks/useReports";
import type { ReportDescriptorView, ReportKind } from "../../types";
import { OCH_QIZIL, sonMatni } from "./hubFormat";

const { Text } = Typography;

/** Davomat maqsadi, foiz — detail sahifadagi maqsad chizig'i bilan bir xil. */
export const ATTENDANCE_GOAL = 95;

/** «Xavf signallari» oynasi (canvas: «14 kun»), bugun bilan birga. */
export const RISK_WINDOW_DAYS = 14;

const TASK_RANG: Record<string, string> = {
	faol: holatVar("kelgan"),
	yakunlangan: holatVar("uzrli"),
	// biome-ignore lint/style/useNamingConvention: backend status enum qiymati aynan shu
	bekor_qilingan: holatVar("neytral"),
};

type Range = { dateFrom: string; dateTo: string };

export interface OverviewStripProps {
	descriptors: ReportDescriptorView[];
	/** Markaz davri — oylik davomat va topshiriqlar kartasi. */
	range: Range;
	/** Bugungi sana (`YYYY-MM-DD`) — «bugun» va «14 kun» kartalari. */
	today: string;
}

/**
 * «Bir qarashda» qatori — to'rt karta, har biri o'z hisobotining
 * analitikasidan.
 *
 * ⚠️ Karta faqat descriptor ro'yxatida bor kind uchun chiziladi va
 * so'rov xatosida (403 doira, 404) JIMGINA yashiriladi — xato matni
 * ko'rsatilmaydi (reja D1).
 *
 * ⚠️ «Xavf signallari» FAQAT `violations` dan: u barcha shubha
 * turlarini (jumladan fake location) o'z ichiga oladi, ya'ni
 * `fake_location` bilan qo'shilsa hodisalar IKKI MARTA sanalardi.
 */
export function OverviewStrip({ descriptors, range, today }: OverviewStripProps) {
	const bor = (kind: ReportKind) => descriptors.some((d) => d.kind === kind);

	return (
		<div data-overview className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-4">
			{bor("attendance_monthly") ? <RateCard range={range} /> : null}
			{bor("attendance_daily") ? <StatusTodayCard today={today} /> : null}
			{bor("violations") ? <RiskCard today={today} /> : null}
			{bor("tasks") ? <TasksCard range={range} /> : null}
		</div>
	);
}

function OvCard({ kind, children }: { kind: string; children: ReactNode }) {
	return (
		<Card size="small" data-overview-card={kind} className="h-full">
			<div className="flex h-full flex-col gap-2">{children}</div>
		</Card>
	);
}

function Sarlavha({ title, aside }: { title: string; aside?: string }) {
	return (
		<div className="flex items-baseline justify-between gap-2">
			<Text type="secondary" className="text-[12.5px]">
				{title}
			</Text>
			{aside ? (
				<span className="text-[11px]" style={{ color: "var(--chart-axis)" }}>
					{aside}
				</span>
			) : null}
		</div>
	);
}

function RateCard({ range }: { range: Range }) {
	const { t } = useTranslation("reports");
	const { data, isError } = useReportAnalytics("attendance_monthly", range, true);
	const rate = data?.totals.find((x) => x.key === "rate");

	if (isError || !data || !rate) {
		return null;
	}

	const nuqtalar = data.spark.find((s) => s.key === "rate")?.points ?? [];
	const farq = rate.previous === null ? null : Math.round((rate.value - rate.previous) * 10) / 10;
	const glif = farq === null ? "" : farq > 0 ? "▲" : farq < 0 ? "▼" : "■";

	return (
		<OvCard kind="attendance_monthly">
			<Sarlavha
				title={t("hub.overview.rateTitle")}
				aside={t("hub.overview.goal", { value: ATTENDANCE_GOAL })}
			/>
			<div className="flex items-baseline gap-2">
				<span className="font-mono font-semibold text-2xl">{sonMatni(rate.value)}%</span>
				{farq === null ? null : (
					<span
						className="text-xs"
						data-kpi-direction={farq > 0 ? "up" : farq < 0 ? "down" : "flat"}
					>
						{glif} {sonMatni(Math.abs(farq))} {t("kpi.ppSuffix")}
					</span>
				)}
			</div>
			{nuqtalar.length > 1 ? (
				<Sparkline
					points={nuqtalar}
					color={holatVar("kelgan")}
					height={50}
					area
					min={Math.max(0, Math.floor(Math.min(ATTENDANCE_GOAL, ...nuqtalar)) - 5)}
					max={100}
					goal={ATTENDANCE_GOAL}
					ariaLabel={`${t("hub.overview.rateTitle")}: ${nuqtalar.map(sonMatni).join(", ")}`}
				/>
			) : null}
		</OvCard>
	);
}

function StatusTodayCard({ today }: { today: string }) {
	const { t } = useTranslation("reports");
	const { data, isError } = useReportAnalytics(
		"attendance_daily",
		{ dateFrom: today, dateTo: today },
		true
	);

	if (isError || !data) {
		return null;
	}

	const jami = data.statusMix.reduce((a, s) => a + s.days, 0);

	return (
		<OvCard kind="attendance_daily">
			<Sarlavha
				title={t("hub.overview.statusTitle")}
				aside={t("hub.overview.shifts", { count: jami })}
			/>
			{jami > 0 ? (
				<SegmentBar
					height={22}
					segments={data.statusMix.map((s) => ({
						key: s.status,
						value: s.days,
						color: holatVar(ATTENDANCE_STATUS_RANG[s.status] ?? "neytral"),
						label: t(`analytics.breakdown.${s.status}`),
					}))}
				/>
			) : (
				<Text type="secondary" className="text-xs">
					{t("hub.card.noData")}
				</Text>
			)}
		</OvCard>
	);
}

function RiskCard({ today }: { today: string }) {
	const { t } = useTranslation("reports");
	const oyna = {
		dateFrom: dayjs(today)
			.subtract(RISK_WINDOW_DAYS - 1, "day")
			.format("YYYY-MM-DD"),
		dateTo: today,
	};
	const { data, isError } = useReportAnalytics("violations", oyna, true);

	if (isError || !data) {
		return null;
	}

	const jami = data.totals.find((x) => x.key === "events")?.value ?? 0;
	const yuqori = data.totals.find((x) => x.key === "high")?.value ?? 0;
	const yuqoriYorliq = t("analytics.totals.high");

	return (
		<OvCard kind="violations">
			<Sarlavha
				title={t("hub.overview.riskTitle", { days: RISK_WINDOW_DAYS })}
				aside={t("hub.overview.riskMeta")}
			/>
			<div className="flex items-baseline gap-2">
				<span
					className="font-mono font-semibold text-2xl"
					style={{ color: jami > 0 ? holatVar("kelmagan") : undefined }}
				>
					{sonMatni(jami)}
				</span>
				<Text type="secondary" className="text-xs">
					{t("hub.overview.riskHigh", { count: yuqori })}
				</Text>
			</div>
			<ColumnChart
				height={40}
				empty={t("hub.card.noData")}
				columns={data.daily.map((kun) => ({
					key: kun.date,
					label: "",
					stack: [
						{ value: kun.flagged, color: holatVar("kelmagan"), label: yuqoriYorliq },
						{
							value: Math.max(0, kun.primary - kun.flagged),
							color: OCH_QIZIL,
							label: t("hub.card.rest"),
						},
					],
					tooltip: `${dayjs(kun.date).format("DD.MM")} · ${sonMatni(kun.primary)} / ${yuqoriYorliq.toLocaleLowerCase()} ${sonMatni(kun.flagged)}`,
				}))}
			/>
		</OvCard>
	);
}

function TasksCard({ range }: { range: Range }) {
	const { t } = useTranslation("reports");
	const { data, isError } = useReportAnalytics("tasks", range, true);

	if (isError || !data) {
		return null;
	}

	const jami = data.totals.find((x) => x.key === "tasks")?.value ?? 0;

	return (
		<OvCard kind="tasks">
			<Sarlavha title={t("hub.overview.tasksTitle")} />
			<Donut
				size={92}
				centerValue={sonMatni(jami)}
				centerLabel={t("hub.overview.tasksCenter")}
				empty={t("hub.card.noData")}
				formatValue={sonMatni}
				segments={data.breakdown.map((b) => ({
					key: b.key,
					value: b.count,
					color: TASK_RANG[b.key] ?? holatVar("neytral"),
					label: t(`analytics.breakdown.${b.key}`),
				}))}
			/>
		</OvCard>
	);
}
